<template>
  <AdminLayout>
    <template #content>
      <div class="d-flex justify-content-between align-items-center mb-3">
        <div>
          <h1 class="h4 mb-1">Rayons ({{ totalRows }})</h1>
          <small class="text-muted">
            Gestion des rayons de stock et de rangement des produits.
          </small>
        </div>

        <div class="d-flex gap-2">
          <ModuleActivityButton :module="['rayons', 'Rayon']" label="Rayons" />

          <button
            type="button"
            class="btn btn-outline-secondary btn-sm btn-icon-label"
            :class="{ active: filtersVisible }"
            :title="filtersVisible ? 'Masquer les filtres' : 'Afficher les filtres'"
            @click="toggleFilters"
          >
            <i class="fa" :class="filtersVisible ? 'fa-eye-slash' : 'fa-filter'"></i>
            <span class="btn-label ms-1">Filtres</span>
          </button>

          <button class="btn btn-primary btn-sm btn-icon-label" type="button" @click="openCreateDialog">
            <i class="fa fa-plus"></i>
            <span class="btn-label ms-1">Nouveau rayon</span>
          </button>
        </div>
      </div>

      <div v-if="errorMessage" class="alert alert-danger mb-3 py-2">
        {{ errorMessage }}
      </div>
      <div v-if="successMessage" class="alert alert-success mb-3 py-2">
        {{ successMessage }}
      </div>

      <!-- Filtres -->
      <div v-show="filtersVisible" class="card module-filter-card mb-3">
        <div class="card-body py-2">
      <div class="row g-2 align-items-end">
        <div class="col-md-3 d-flex align-items-center">
          <div class="form-check d-flex align-items-center gap-2 ps-0">
            <PrimevueCheckbox v-model="filters.only_active" binary inputId="onlyActiveRays" @change="fetchRays" />
            <label class="form-check-label small" for="onlyActiveRays">
              Afficher uniquement les rayons actifs
            </label>
          </div>
        </div>
        <div class="col-12 module-filter-actions mt-2">
          <button type="button" class="btn btn-outline-secondary btn-sm btn-icon-label" @click="resetFilters">
            <i class="fa fa-rotate-left"></i>
            <span class="btn-label ms-1">Réinitialiser</span>
          </button>
          <button type="button" class="btn btn-primary btn-sm btn-icon-label" @click="fetchRays">
            <i class="fa fa-search"></i>
            <span class="btn-label ms-1">Filtrer</span>
          </button>
        </div>
      </div>
        </div>
      </div>

      <BootstrapDataTable
        :rows="filteredRays"
        :columns="tableColumns"
        :loading="isLoading"
        :selectable="canBulkUpdateRays"
        v-model:selected-row-ids="bulkSelectedIds"
        :is-row-selectable="canSelectRay"
        :selection-disabled="bulkActionPending"
        :sort-by="table.sortBy"
        :sort-dir="table.sortDir"
        :active-filter-count="activeFilterCount"
        state-key="rayons.list"
        searchable
        v-model:search-value="filters.search"
        search-placeholder="Rechercher nom ou code..."
        detail-route-name="RayShow"
        empty-text="Aucun rayon trouvé."
        @search-change="handleTableSearch"
        @sort-change="handleSortChange"
        @refresh="fetchRays"
      >
        <template #bulk-actions="{ selectedCount, isOverLimit }">
          <button type="button" class="btn btn-sm btn-outline-success" :disabled="bulkActionPending || isOverLimit" @click="bulkSetRayStatus('activate')">
            <i class="fa fa-check me-1"></i>Activer {{ selectedCount }}
          </button>
          <button type="button" class="btn btn-sm btn-outline-danger" :disabled="bulkActionPending || isOverLimit" @click="bulkSetRayStatus('deactivate')">
            <i class="fa fa-ban me-1"></i>Désactiver {{ selectedCount }}
          </button>
        </template>

        <template #cell-position="{ row }">
          {{ row.position ?? 0 }}
        </template>

        <template #cell-products_count="{ row }">
          <span class="badge bg-light text-dark">
            {{ row.products_count || 0 }}
          </span>
        </template>

        <template #cell-color="{ row }">
          <span class="rayon-color-cell">
            <span class="rayon-color-dot" :style="{ backgroundColor: rayonColor(row.color) }"></span>
            <span>{{ rayonColor(row.color) }}</span>
          </span>
        </template>

        <template #cell-is_active="{ row }">
          <span class="badge" :class="row.is_active ? 'bg-success' : 'bg-secondary'">
            {{ row.is_active ? 'Oui' : 'Non' }}
          </span>
        </template>
      </BootstrapDataTable>

      <!-- Dialog création / édition -->
      <PrimevueDialog v-model:visible="formDialogVisible" :modal="true" :style="{ width: '600px' }" :closable="false">
        <template #header>
          <h5 class="mb-0">
            {{ formDialogMode === 'create' ? 'Nouveau rayon' : 'Modifier le rayon' }}
          </h5>
        </template>

        <RayForm :mode="formDialogMode" :rayon="selectedRay" :submitting="isSavingForm" @save="handleSave" @cancel="closeFormDialog" />
      </PrimevueDialog>
    </template>
  </AdminLayout>
</template>

<script>
import RayForm from './RayForm.vue';
import {
  countActiveFilters,
  loadModuleFilterState,
  saveModuleFilterValues,
  saveModuleFilterVisibility,
} from '@/support/moduleFilters';
import { normalizeRayonColor } from '@/utils/rayonColor';
import { bulkActionViewMixin } from '@/support/bulkActions';
import { hasPermission } from '@/support/permissions';

const FILTER_MODULE = 'rayons';
const DEFAULT_FILTERS = {
  search: '',
  only_active: true,
};

export default {
  name: 'RaysView',
  mixins: [bulkActionViewMixin],
  components: {
    RayForm,
  },
  data() {
    return {
      rays: [],
      isLoading: false,
      errorMessage: '',
      successMessage: '',
      filters: { ...DEFAULT_FILTERS },
      filtersVisible: false,
      table: {
        sortBy: 'position',
        sortDir: 'asc',
      },
      tableColumns: [
        { key: 'position', label: '#', sortable: true, class: 'text-center', style: 'width: 70px;' },
        { key: 'name', label: 'Nom', sortable: true },
        { key: 'code', label: 'Code', sortable: true },
        { key: 'color', label: 'Couleur', sortable: true },
        { key: 'products_count', label: 'Produits', sortable: true, class: 'text-center' },
        { key: 'is_active', label: 'Actif', sortable: true, class: 'text-center' },
      ],

      formDialogVisible: false,
      formDialogMode: 'create', // 'create' | 'edit'
      selectedRay: null,
      isSavingForm: false,
    };
  },
  computed: {
    currentUser() {
      return this.$store?.getters?.['auth/currentUser'] || null;
    },
    canBulkUpdateRays() {
      return hasPermission(this.currentUser, 'products.update');
    },
    totalRows() {
      return this.filteredRays.length;
    },
    activeFilterCount() {
      return countActiveFilters(this.filters, DEFAULT_FILTERS, ['search']);
    },
    filteredRays() {
      const term = this.filters.search.trim().toLowerCase();
      if (!term) return this.rays;

      return this.rays.filter((r) => {
        return (
          (r.name && r.name.toLowerCase().includes(term)) ||
          (r.code && r.code.toLowerCase().includes(term))
        );
      });
    },
  },
  mounted() {
    this.restoreFilters();
    this.fetchRays();
  },
  watch: {
    filters: {
      deep: true,
      handler(filters) {
        saveModuleFilterValues(FILTER_MODULE, filters);
      },
    },
    filtersVisible(value) {
      saveModuleFilterVisibility(FILTER_MODULE, value);
    },
  },
  methods: {
    canSelectRay(row) {
      return row?.type !== 'system' && !row?.system_key;
    },

    async bulkSetRayStatus(action) {
      const isActivation = action === 'activate';

      await this.performBulkAction({
        url: `/rayons/bulk/${action}`,
        method: 'patch',
        itemLabel: 'rayon',
        confirmation: (count) => ({
          header: isActivation ? 'Activer les rayons' : 'Désactiver les rayons',
          message: `${isActivation ? 'Activer' : 'Désactiver'} les ${count} rayons sélectionnés ?`,
          acceptLabel: `${isActivation ? 'Activer' : 'Désactiver'} ${count}`,
          acceptClass: isActivation ? 'p-button-success' : 'p-button-danger',
        }),
        refresh: () => this.fetchRays(),
      });
    },

    restoreFilters() {
      const state = loadModuleFilterState(FILTER_MODULE, DEFAULT_FILTERS);
      this.filters = state.values;
      this.filtersVisible = state.visible;
    },

    toggleFilters() {
      this.filtersVisible = !this.filtersVisible;
    },

    resetFilters() {
      this.filters = { ...DEFAULT_FILTERS };
      this.fetchRays();
    },

    handleTableSearch(search) {
      this.filters.search = search;
    },

    rayonColor(color) {
      return normalizeRayonColor(color);
    },

    async fetchRays(options = {}) {
      this.isLoading = true;
      this.errorMessage = '';
      if (!options.preserveSuccess) {
        this.successMessage = '';
      }

      try {
        const res = await this.$api.get('/rayons', {
          params: { only_active: this.filters.only_active ? 'true' : 'false' },
        });
        this.rays = res.data.data || res.data || [];
      } catch (e) {
        console.error('Erreur chargement rayons', e);
        this.errorMessage = 'Impossible de charger les rayons.';
      } finally {
        this.isLoading = false;
      }
    },

    openCreateDialog() {
      this.formDialogMode = 'create';
      this.selectedRay = null;
      this.formDialogVisible = true;
    },

    openEditDialog(row) {
      this.formDialogMode = 'edit';
      this.selectedRay = row;
      this.formDialogVisible = true;
    },

    closeFormDialog() {
      this.formDialogVisible = false;
      this.selectedRay = null;
    },

    async handleSave(payload) {
      if (this.isSavingForm) return;

      this.errorMessage = '';
      this.successMessage = '';
      this.isSavingForm = true;

      try {
        if (this.formDialogMode === 'create') {
          const response = await this.$api.post('/rayons', payload);
          this.successMessage = this.creationSuccessMessage(
            'Rayon créé avec succès.',
            response.data?.data?.replication_summary
          );
        } else {
          const id = payload.id || (this.selectedRay && this.selectedRay.id);
          await this.$api.put(`/rayons/${id}`, payload);
          this.successMessage = 'Rayon mis à jour avec succès.';
        }

        this.formDialogVisible = false;
        await this.fetchRays({ preserveSuccess: true });
      } catch (error) {
        console.error('Erreur enregistrement rayon', error);
        let msg = 'Une erreur est survenue lors de la sauvegarde du rayon.';
        if (
          error &&
          error.response &&
          error.response.data &&
          error.response.data.errors
        ) {
          const rawErrors = Object.values(error.response.data.errors);
          const flat = rawErrors.reduce((acc, v) => acc.concat(v), []);
          msg = flat.join(' ');
        }
        this.errorMessage = msg;
      } finally {
        this.isSavingForm = false;
      }
    },

    async deleteRay(row) {
      if ((row.products_count || 0) > 0) {
        alert(
          "Impossible de supprimer ce rayon car des produits y sont associés. " +
          'Vous pouvez déplacer les produits vers un autre rayon ou désactiver ce rayon.'
        );
        return;
      }

      const ok = await this.$confirmAction(`Supprimer le rayon "${row.name}" ?`);
      if (!ok) return;

      this.errorMessage = '';
      this.successMessage = '';

      try {
        await this.$api.delete(`/rayons/${row.id}`);
        this.successMessage = 'Rayon supprimé.';
        await this.fetchRays({ preserveSuccess: true });
      } catch (error) {
        console.error('Erreur suppression rayon', error);
        let msg = "Une erreur est survenue lors de la suppression du rayon.";
        if (
          error &&
          error.response &&
          error.response.data &&
          error.response.data.errors
        ) {
          const rawErrors = Object.values(error.response.data.errors);
          const flat = rawErrors.reduce((acc, v) => acc.concat(v), []);
          msg = flat.join(' ');
        }
        this.errorMessage = msg;
      }
    },

    handleSortChange({ sortBy, sortDir }) {
      this.table.sortBy = sortBy;
      this.table.sortDir = sortDir;
    },

    creationSuccessMessage(baseMessage, summary) {
      if (!summary) {
        return baseMessage;
      }

      const created = Number(summary.created_count || 0);
      const skipped = Number(summary.skipped_count || 0);
      const parts = [`${baseMessage} Créé dans ${created} autre(s) site(s).`];

      if (skipped > 0) {
        parts.push(`${skipped} site(s) ignoré(s) car un rayon du même nom existe déjà.`);
      }

      return parts.join(' ');
    },
  },
};
</script>

<style scoped>
.rayon-color-cell {
  align-items: center;
  display: inline-flex;
  gap: 0.45rem;
}

.rayon-color-dot {
  border: 1px solid rgba(15, 23, 42, 0.16);
  border-radius: 999px;
  display: inline-block;
  height: 0.8rem;
  width: 0.8rem;
}
</style>
